// Checkout — plan selection + pay-as-you-go top-up.
//
// Two distinct flows depending on whether the user has an existing
// subscription:
//
// FREE USER (no existing subscription) — click any plan → Subscribe button
// creates a Stripe Checkout Session and redirects.
//
// PAID USER (existing subscription, plan_tier != 'free') — switching plans
// or cancelling can't happen via "create a new subscription" (that'd give
// them two subscriptions). They get redirected through Stripe's hosted
// Customer Portal where Stripe handles plan switching, cancellation,
// proration, and payment method updates. Webhook events flow back to
// Supabase via apply_stripe_subscription / apply_stripe_cancellation.
//
// Top-ups (one-time $10 / 50 credits) work the same for everyone — direct
// Stripe Checkout in 'payment' mode. Not a subscription.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PLANS,
  TOP_UPS,
  yearlySavingsPct,
  type PlanId,
  type Plan,
  type BillingInterval,
} from '@/hooks/useSubscription';
import { useCredits } from '@/hooks/useCredits';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/hooks/useConfirm';
import { stashPostAuthRedirect } from '@/lib/postAuthRedirect';
import { Logo } from '@/components/feature/Logo';
import {
  activateTrial,
  createCheckoutSession,
  createCustomerPortalSession,
  type CheckoutPlanId,
} from '@/lib/api';

// Plan ordering for upgrade/downgrade label logic. Index = "tier level"
// — higher = more expensive. Matches Stripe Price IDs ordering, which
// matches the price ladder ($19 < $49 < $129).
const PLAN_ORDER: Record<string, number> = {
  'free':     0,
  'starter':  1,
  'pro':      2,
  'business': 3,
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Real plan_tier comes from Supabase via useCredits — that's the source of
  // truth (webhook keeps it in sync with Stripe).
  const { balance, planTier, subscriptionEndsAt, trialEndsAt, refresh } = useCredits();
  const { user, loading: authLoading } = useAuth();
  const confirm = useConfirm();
  const isPaidUser = planTier !== null && planTier !== 'free';
  // Trialing = a paid tier whose trial hasn't ended yet. During a trial the
  // user has plan ACCESS but hasn't been charged, so we treat them as NOT a
  // settled subscriber for the "your plan" / top-up / manage surfaces.
  const isTrialing = !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();
  const trialDaysLeft = isTrialing
    ? Math.max(1, Math.ceil((new Date(trialEndsAt!).getTime() - Date.now()) / 86_400_000))
    : 0;
  // A real, converted subscriber (card already charged) — the only state that
  // sees top-ups + the settled "your plan / manage" UI.
  const isSettledPaid = isPaidUser && !isTrialing;

  // Default plan selection priority:
  //   1. ?plan= query string (deep-linked from another page)
  //   2. The user's CURRENT plan (so Business users see Business pre-selected,
  //      not Pro — they're presumably here to switch or just browsing)
  //   3. 'pro' as a fallback hero choice for new users with no plan
  const preselectedPlan = (searchParams.get('plan') as PlanId)
    || (isPaidUser ? (planTier as PlanId) : 'pro');
  const [selectedPlan, setSelectedPlan] = useState<PlanId>(preselectedPlan);

  // Deep-link scroll: when arriving via a "Start Free Trial" CTA (?plan=X) —
  // from the hero or a pricing card — bring the chosen plan card into view
  // instead of parking the visitor at the top of the page. Only fires when a
  // plan was explicitly requested; a plain /checkout visit is left untouched.
  const selectedCardRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!searchParams.get('plan')) return;
    const t = setTimeout(() => {
      selectedCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Billing interval toggle — defaults to MONTHLY (lower commitment / friction
  // for a first trial). Users can switch to yearly for the cheaper rate; credits
  // still refill every month on both. Honors an ?interval= param so a Yearly
  // choice made before sign-up survives the resume round-trip (see the stash in
  // handleSubscribe + the auto-resume effect below).
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(
    searchParams.get('interval') === 'yearly' ? 'yearly' : 'monthly',
  );
  const [loading, setLoading] = useState(false);
  // Top-up button has its own loading state since clicking a top-up shouldn't
  // grey out the main Subscribe CTA (or vice versa).
  const [topupLoadingId, setTopupLoadingId] = useState<string | null>(null);
  // Manage Subscription button (opens Customer Portal) — separate loading
  // state since both it and Subscribe shouldn't be greyed simultaneously.
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "Activate now" blocking-modal state. The activate endpoint returns as soon
  // as Stripe settles the charge, but the 100 credits + trial-clear land via the
  // ASYNC invoice.paid webhook. We keep a full-screen spinner up (blocking any
  // navigation) and poll the profile until the grant lands, THEN open the app —
  // so the user never sees a stale balance or wanders off mid-charge.
  const [activating, setActivating] = useState(false);
  const activateStartRef = useRef(0);
  // Mirror trialEndsAt into a ref so the self-contained poll loop below can read
  // the freshest value without being re-created on every credits change.
  const trialEndsAtRef = useRef(trialEndsAt);
  trialEndsAtRef.current = trialEndsAt;

  // Shared launcher: create a Stripe Checkout Session on the backend, then
  // redirect. Used by Subscribe (free user) + Top-up. Interval is 'monthly'
  // for the one-time top-up (server ignores it there).
  const launchCheckout = async (
    planId: CheckoutPlanId,
    interval: BillingInterval = 'monthly',
  ) => {
    setError(null);
    try {
      const { url } = await createCheckoutSession(planId, interval);
      window.location.href = url;
    } catch (err) {
      console.error('Checkout session failed:', err);
      const msg = err instanceof Error ? err.message : 'Checkout failed';
      setError(msg);
      setLoading(false);
      setTopupLoadingId(null);
    }
  };

  // Customer Portal launcher: for paid users to switch plans / cancel /
  // update payment method. Stripe-hosted UI; webhook updates Supabase.
  const launchCustomerPortal = async () => {
    setError(null);
    setPortalLoading(true);
    try {
      const { url } = await createCustomerPortalSession();
      window.location.href = url;
    } catch (err) {
      console.error('Customer portal session failed:', err);
      const msg = err instanceof Error ? err.message : 'Manage subscription failed';
      setError(msg);
      setPortalLoading(false);
    }
  };

  // Subscribe button click handler. Routes paid users to the portal
  // (where they can switch plans), free users to Stripe Checkout (first
  // subscription). Same button, two destinations based on state.
  const handleSubscribe = async () => {
    if (isPaidUser) {
      await launchCustomerPortal();
      return;
    }
    // Logged-out visitor deep-linked from the marketing pricing cards
    // (/checkout?plan=X). Stripe checkout needs an authenticated user, so stash
    // this exact checkout URL — tagged resume=1 + carrying the chosen plan and
    // billing interval — and send them to sign up. Once they authenticate +
    // verify their email, the auth pages consume the stash and drop them back
    // here, where the auto-resume effect below sends them straight into Stripe
    // (they already picked a plan + committed — no re-selection). See
    // lib/postAuthRedirect.ts.
    if (!authLoading && !user) {
      stashPostAuthRedirect(
        `/checkout?plan=${selectedPlan}&interval=${billingInterval}&resume=1`,
      );
      navigate('/auth/sign-up');
      return;
    }
    setLoading(true);
    await launchCheckout(selectedPlan as CheckoutPlanId, billingInterval);
  };

  const handleTopup = async (topupId: string) => {
    setTopupLoadingId(topupId);
    await launchCheckout(topupId as CheckoutPlanId);
  };

  // Auto-resume: a visitor who deep-linked from the pricing cards and clicked
  // "Start free trial" while logged out was stashed to
  // /checkout?plan=X&interval=Y&resume=1, sent through sign-up + email verify,
  // then dropped back here. They already chose a plan and already committed to
  // starting the trial — so don't make them re-select + click again; resume the
  // action straight into Stripe. Fires exactly once. Plain /checkout visits
  // (nav "Sign Up", or browsing) carry no resume flag, so the plan picker shows
  // as normal. Guarded on authLoading so we don't fire before auth resolves.
  const autoResumedRef = useRef(false);
  useEffect(() => {
    if (autoResumedRef.current) return;
    if (authLoading || !user) return;      // wait for a real, signed-in user
    if (isPaidUser) return;                 // paid users go through the portal
    if (searchParams.get('resume') !== '1') return;
    autoResumedRef.current = true;
    setLoading(true);
    void launchCheckout(selectedPlan as CheckoutPlanId, billingInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, isPaidUser]);

  const selected = PLANS.find((p) => p.id === selectedPlan)!;
  const isCurrent = selectedPlan === planTier;
  const isYearly = billingInterval === 'yearly';

  // Interval-aware price + period for a plan.
  const priceFor = (p: Plan) => (isYearly ? p.priceYearly : p.price);
  const periodLabel = isYearly ? 'year' : 'month';

  // Biggest yearly discount across plans — drives the "Save up to N%" badge
  // on the Yearly toggle button.
  const maxYearlySavings = Math.max(...PLANS.map((p) => yearlySavingsPct(p)));

  // Compute the button label based on direction relative to user's plan.
  // Free user: always "Subscribe to X". Paid user: "Upgrade to X" /
  // "Downgrade to X" depending on price ladder. Same-plan: "Your current
  // plan" (handled via the isCurrent disabled branch).
  const subscribeLabel = (() => {
    const priceLabel = `$${priceFor(selected)}/${periodLabel}`;
    // Freemium: the free tier is the trial, so paid plans subscribe immediately
    // (charged today, no 7-day trial). Paid users (upgrade / downgrade / switch)
    // go through the Customer Portal.
    if (!isPaidUser) return `Subscribe to ${selected.name} · ${priceLabel}`;
    const currentLevel = PLAN_ORDER[planTier ?? 'free'] ?? 0;
    const targetLevel = PLAN_ORDER[selectedPlan] ?? 0;
    if (targetLevel > currentLevel) return `Upgrade to ${selected.name} for ${priceLabel}`;
    if (targetLevel < currentLevel) return `Downgrade to ${selected.name} for ${priceLabel}`;
    return `Switch to ${selected.name} for ${priceLabel}`;
  })();

  // Activate an in-progress trial immediately: end the Stripe trial now so the
  // card is charged and the user gets their full monthly credits (the grant +
  // trial-clear flow through the existing invoice.paid webhook). Confirmed first
  // since it moves the charge forward + ends the free days early.
  const handleActivateTrial = async () => {
    const ok = await confirm({
      title: 'Activate your plan now?',
      message: (
        <>
          Your free trial will end and your card will be charged now. You&apos;ll get
          your full{' '}
          <span className="font-bold">
            {selected.creditsPerMonth.toLocaleString()} monthly credits
          </span>{' '}
          right away.
        </>
      ),
      confirmLabel: 'Activate now',
    });
    if (!ok) return;
    setError(null);
    // Open the blocking modal; the poll effect below keeps it up until the
    // async webhook grant lands (trial clears), then opens the app.
    activateStartRef.current = Date.now();
    setActivating(true);
    try {
      await activateTrial();
    } catch (err) {
      console.error('Activate trial failed:', err);
      setActivating(false);
      setError(err instanceof Error ? err.message : 'Could not activate plan');
    }
  };

  // While activating, poll the profile until the invoice.paid webhook has
  // granted the full credits (signalled by trial_ends_at clearing), or a safety
  // timeout, then open the app. Self-contained recursive poll — NOT driven by
  // state changes — so it keeps ticking even when the trial date is unchanged
  // between refreshes. Reads the freshest value via trialEndsAtRef (checked at
  // the top of each tick, after the previous refresh's re-render has flushed).
  useEffect(() => {
    if (!activating) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      if (cancelled) return;
      if (trialEndsAtRef.current === null) {              // grant landed → trial cleared
        setActivating(false);
        navigate('/create');
        return;
      }
      if (Date.now() - activateStartRef.current > 25_000) {  // safety timeout
        setActivating(false);
        navigate('/create');
        return;
      }
      await refresh();                                    // re-fetch → updates trialEndsAtRef on re-render
      if (!cancelled) timer = setTimeout(tick, 1500);
    };
    timer = setTimeout(tick, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activating, refresh, navigate]);

  // Resume flow: while we're auth-resolving or launching Stripe, show a full-page
  // "redirecting" screen instead of the plan picker so it never flashes before
  // the redirect. Falls back to the picker if there's an error, or if the user
  // turns out not to be an eligible (signed-in, free) resumer.
  const wantsResume = searchParams.get('resume') === '1';
  const showResumeLoader =
    wantsResume && !error && (authLoading || (!!user && !isPaidUser));

  if (showResumeLoader) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6 text-center">
        <Logo height={56} />
        <div className="mt-8 w-8 h-8 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
        <h1 className="mt-6 font-display font-bold text-dark text-xl">
          Starting your subscription…
        </h1>
        <p className="mt-2 text-dark-muted text-sm">
          Taking you to secure checkout.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      {/* Blocking activation modal — full-screen, non-dismissible. Covers the
          whole page (intercepts all clicks) while we charge the card + wait for
          the async credit grant, then opens the app with the fresh balance. */}
      {activating && (
        <div className="fixed inset-0 z-[100] bg-surface/90 backdrop-blur-sm flex flex-col items-center justify-center px-6 text-center">
          <Logo height={56} />
          <div className="mt-8 w-8 h-8 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
          <p className="mt-6 text-base font-bold text-dark">Activating your plan…</p>
          <p className="mt-2 text-dark-muted text-sm max-w-sm">
            Charging your card and unlocking your full monthly credits. Hang tight —
            we&apos;ll open the app for you.
          </p>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-surface-dark/40">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center">
            <Logo height={56} />
          </a>
          {/* Cancel only in the paid "manage / switch plan" context. The
              trial-start (free) view has no escape button — the trial is the
              way forward; the Logo (→ home) is still the way out. Also avoids
              the verify-email → checkout → back redirect loop for new signups. */}
          {isPaidUser && (
            <button
              onClick={() => navigate(-1)}
              className="text-sm font-medium text-dark-muted hover:text-dark transition-colors cursor-pointer"
            >
              Cancel
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 py-10">
        {/* Prominent current-balance card — shown above plan cards so users
            see their actual credit count immediately. The plan cards below
            show static monthly allotments (e.g. "100 credits per month")
            which can be confusing when a user has more credits than their
            plan grants (e.g. after a top-up purchase). This card is the
            unambiguous answer to "how many credits do I have right now?". */}
        {isSettledPaid && balance !== null && (
          <div className="mb-8 p-5 rounded-2xl bg-white border border-primary/20 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-light flex items-center justify-center flex-shrink-0">
              <i className="ri-coin-line text-primary text-2xl" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-dark-muted text-xs uppercase tracking-wide font-semibold">
                Your current balance
              </p>
              <p className="font-display font-bold text-dark text-2xl tabular-nums">
                {balance.toLocaleString()} credit{balance === 1 ? '' : 's'}
              </p>
              {isPaidUser && (
                <p className="text-dark-muted text-xs mt-0.5">
                  Refills to your plan's monthly amount on each renewal. Top-ups stack on top.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="text-center mb-6">
          <h1 className="font-display font-bold text-dark text-2xl mb-2">
            {isSettledPaid ? 'Choose Your Plan' : isTrialing ? 'Your free trial' : 'Choose your plan'}
          </h1>
          <p className="text-dark-muted text-sm">
            {isSettledPaid
              ? 'Credits cover all generations. Low = 1 credit, Medium = 2, High = 4. Carousels multiply by 4 frames.'
              : isTrialing
              ? 'You have full access during your trial. Out of credits? Activate now to start your subscription and unlock your full monthly credits.'
              : 'Pick a plan to unlock Pro features and monthly credits — billed monthly, cancel anytime.'}
          </p>
        </div>

        {/* Monthly / Yearly billing toggle. Defaults to Yearly (cheaper).
            Credits refill every month on BOTH — yearly is just a lower rate. */}
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white border border-surface-dark/40">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer ${
                !isYearly ? 'bg-primary text-white' : 'text-dark-muted hover:text-dark'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('yearly')}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer ${
                isYearly ? 'bg-primary text-white' : 'text-dark-muted hover:text-dark'
              }`}
            >
              Yearly
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  isYearly ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-700'
                }`}
              >
                Save {maxYearlySavings}%
              </span>
            </button>
          </div>
          <p className="text-dark-muted text-xs">
            {isYearly
              ? 'Billed once a year at a lower rate — credits still refill every month.'
              : 'Billed monthly — switch to yearly to save.'}
          </p>
        </div>

        {/* Cancellation notice — surfaced at the top of /checkout for paid users
            who have cancelled. Mirrors the banner on /auth/profile. */}
        {subscriptionEndsAt && isSettledPaid && (
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200 mb-8">
            <i className="ri-error-warning-line text-amber-600 text-lg flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900 text-sm">
                Your subscription is scheduled to end on{' '}
                {new Date(subscriptionEndsAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}.
              </p>
              <p className="text-amber-700 text-xs mt-1">
                You'll keep full access until then. To reactivate or switch plans, use Manage subscription below.
              </p>
            </div>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {PLANS.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const isActiveOnAccount = planTier === plan.id;
            return (
              <button
                key={plan.id}
                ref={isSelected ? selectedCardRef : undefined}
                onClick={() => {
                  // A trialing user's own plan card → activate now (start paying).
                  if (isTrialing && planTier === plan.id) { void handleActivateTrial(); return; }
                  // Otherwise: first click selects the plan; clicking the already-
                  // selected card proceeds — same destination as the summary CTA.
                  if (selectedPlan === plan.id) { void handleSubscribe(); return; }
                  setSelectedPlan(plan.id);
                }}
                className={`relative text-left rounded-2xl border-2 p-6 transition-all cursor-pointer ${
                  isSelected
                    ? 'border-primary bg-white shadow-lg shadow-primary/10'
                    : 'border-surface-dark/40 bg-white hover:border-primary/30'
                } ${plan.isHero ? 'md:scale-[1.03]' : ''}`}
              >
                {plan.isHero && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-primary to-fuchsia-500 text-white text-xs font-bold uppercase tracking-wide">
                    Most Popular
                  </span>
                )}
                {isActiveOnAccount && (
                  isTrialing ? (
                    <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wide">
                      Trial · {trialDaysLeft}d
                    </span>
                  ) : subscriptionEndsAt ? (
                    // Cancellation scheduled — subscription_ends_at is set. Show an
                    // orange "expiring on {date}" pill instead of the green "Current".
                    <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wide whitespace-nowrap">
                      Expires {new Date(subscriptionEndsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  ) : (
                    <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wide">
                      Current
                    </span>
                  )
                )}

                <h3 className="font-display font-bold text-dark text-lg mb-1">{plan.name}</h3>
                <p className="text-dark-muted text-xs mb-4 min-h-[2.5rem]">{plan.description}</p>

                {isYearly ? (
                  <>
                    <div className="flex items-baseline gap-1 mb-0.5">
                      <span className="font-display font-bold text-dark text-3xl">
                        ${plan.priceYearly.toLocaleString()}
                      </span>
                      <span className="text-dark-muted text-xs">/year</span>
                    </div>
                    <p className="text-xs text-emerald-600 font-semibold mb-1">
                      ≈ ${Math.round(plan.priceYearly / 12)}/mo · Save {yearlySavingsPct(plan)}%
                    </p>
                  </>
                ) : (
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-display font-bold text-dark text-3xl">
                      ${plan.price}
                    </span>
                    <span className="text-dark-muted text-xs">/month</span>
                  </div>
                )}
                <p className="text-xs text-primary font-semibold mb-5">
                  {plan.creditsPerMonth.toLocaleString()} credits / month
                </p>

                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-dark">
                      <i className="ri-check-line text-primary mt-0.5 flex-shrink-0 text-xs" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className={`mt-5 w-full py-2.5 rounded-full text-center text-xs font-bold transition-all ${
                  isSelected
                    ? 'bg-primary text-white'
                    : 'bg-surface text-dark-muted'
                }`}>
                  {isActiveOnAccount
                    ? (isTrialing ? 'Activate plan now' : 'Your Plan')
                    : isSelected
                    ? (isSettledPaid
                        ? 'Selected'
                        : isTrialing
                        ? 'Switch to this plan'
                        : 'Subscribe now')
                    : 'Select'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Summary + CTA */}
        <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 mb-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm font-semibold text-dark">{selected.name} Plan</p>
              <p className="text-xs text-dark-muted">
                {selected.creditsPerMonth.toLocaleString()} credits monthly ·{' '}
                {isYearly ? 'Billed yearly' : 'Billed monthly'} · Cancel anytime
              </p>
            </div>
            <div className="text-right">
              {!isPaidUser ? (
                <>
                  <p className="font-display font-bold text-dark text-xl">
                    ${priceFor(selected).toLocaleString()}
                  </p>
                  <p className="text-xs text-dark-muted">due today</p>
                  <p className="text-[11px] text-dark-muted mt-0.5">
                    billed {isYearly ? 'yearly' : 'monthly'} · cancel anytime
                  </p>
                </>
              ) : (
                <>
                  <p className="font-display font-bold text-dark text-xl">
                    ${priceFor(selected).toLocaleString()}
                  </p>
                  <p className="text-xs text-dark-muted">/{periodLabel}</p>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-xs border border-red-200 mb-5">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {isCurrent ? (
            isTrialing ? (
              // Trialing on this plan → activate now (start paying + get the
              // full monthly credits) with a manage / cancel path underneath.
              <>
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 text-amber-900 text-xs border border-amber-200 mb-4">
                  <i className="ri-gift-line text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>
                    You&apos;re on a free trial of {selected.name} — {trialDaysLeft} day
                    {trialDaysLeft === 1 ? '' : 's'} left. Activate now to start your
                    subscription and unlock your full{' '}
                    {selected.creditsPerMonth.toLocaleString()} monthly credits.
                  </span>
                </div>
                <button
                  onClick={handleActivateTrial}
                  disabled={loading || portalLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Activating…
                    </>
                  ) : (
                    <>
                      <i className="ri-flashlight-line" />
                      Activate plan now
                    </>
                  )}
                </button>
                <button
                  onClick={launchCustomerPortal}
                  disabled={portalLoading || loading}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-surface-dark text-dark text-sm font-semibold hover:border-primary/40 hover:text-primary transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {portalLoading ? 'Opening Stripe…' : 'Manage or cancel subscription'}
                </button>
              </>
            ) : (
              // Settled subscriber, same plan selected. Quiet "current plan"
              // badge + "Manage subscription".
              <>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-surface text-dark-muted text-sm mb-3">
                  <i className="ri-check-line text-emerald-600" />
                  You are already on this plan.
                </div>
                {isSettledPaid && (
                  <button
                    onClick={launchCustomerPortal}
                    disabled={portalLoading || loading || topupLoadingId !== null}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-surface-dark text-dark text-sm font-semibold hover:border-primary/40 hover:text-primary transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {portalLoading ? (
                      <>
                        <div className="w-4 h-4 rounded-full border-2 border-dark/20 border-t-dark animate-spin" />
                        Opening Stripe…
                      </>
                    ) : (
                      <>
                        <i className="ri-settings-3-line" />
                        Manage subscription (cancel, update card, invoices)
                      </>
                    )}
                  </button>
                )}
              </>
            )
          ) : (
            // Different plan selected. Subscribe button text + behavior
            // depends on whether the user is currently FREE (Stripe Checkout
            // for first subscription) or already PAID (Customer Portal to
            // switch plans).
            <>
            {!isPaidUser && (
              <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-primary/5 text-dark text-xs border border-primary/15 mb-4">
                <i className="ri-gift-line text-primary mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">${priceFor(selected).toLocaleString()}/{periodLabel}, billed today.</strong>{' '}
                  Your {selected.creditsPerMonth.toLocaleString()} monthly credits are added instantly
                  and refresh every cycle. Cancel anytime — no long-term commitment.
                </span>
              </div>
            )}
            <button
              onClick={handleSubscribe}
              disabled={loading || portalLoading || topupLoadingId !== null}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading || portalLoading ? (
                <>
                  <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  <i className="ri-secure-payment-line" />
                  {subscribeLabel}
                </>
              )}
            </button>
            </>
          )}
        </div>

        {/* Pay-as-you-go top-up — PAID users only. Customers without a plan
            can't buy a boost, so this is hidden in the trial/signup context. */}
        {isSettledPaid && (
        <div className="bg-white rounded-2xl border border-surface-dark/40 p-6">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-coin-line text-primary" />
            <h2 className="font-display font-bold text-dark text-base">
              Need a one-off boost?
            </h2>
          </div>
          <p className="text-dark-muted text-xs mb-5">
            Buy a top-up pack — credits never expire and stack on top of any plan.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {TOP_UPS.map((topup) => {
              const isBusy = topupLoadingId === topup.id;
              return (
                <div
                  key={topup.id}
                  className="flex items-center justify-between p-4 rounded-xl border border-surface-dark/40 bg-surface/40"
                >
                  <div>
                    <p className="font-display font-bold text-dark text-base">
                      +{topup.credits} credits
                    </p>
                    <p className="text-xs text-dark-muted">{topup.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="font-display font-bold text-dark text-lg">
                      ${topup.price}
                    </span>
                    <button
                      onClick={() => handleTopup(topup.id)}
                      disabled={loading || topupLoadingId !== null}
                      className="text-xs font-bold text-white bg-primary hover:bg-primary-hover px-4 py-1.5 rounded-full transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isBusy ? 'Redirecting…' : `Buy $${topup.price}`}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
